import * as React from 'react'; // https://github.com/aelbore/esbuild-jest/issues/61
import Serializer, {
  type SerializeArguments,
  type DeserializeArguments,
} from './Serializer';
import { Node } from 'slate';
import { Prism } from '../../util/prismLanguages';
import isLineBreak from '../helpers/isLineBreak';
import AlignmentUtils from '../../plugins/AlignmentPlugin/AlignmentUtils';
import { isCodeBlockElement } from '../../types/guards';
import { isHTMLElementNode, isTextNode } from '../helpers/serializerHelpers';

function breaksToNewlines(node: HTMLElement) {
  for (const br of node.querySelectorAll('br')) {
    br.outerHTML = '\n';
  }

  // Remove a trailing newline since Slate will add one when it displays the
  // last line of a block.
  const lastNode = node.lastChild;
  if (
    lastNode &&
    isTextNode(lastNode) &&
    lastNode.textContent &&
    lastNode.textContent.endsWith('\n')
  ) {
    lastNode.textContent = lastNode.textContent.slice(0, -1);
  }
}

class SerializerCodeBlock extends Serializer {
  static serialize({ node, children }: SerializeArguments) {
    if (!isCodeBlockElement(node)) return;

    const style = AlignmentUtils.alignmentStyleForElement(node);
    const language = node.code_language;
    const grammar = language && Prism.languages[language];

    if (grammar) {
      let highlightedText = Prism.highlight(
        Node.string(node),
        grammar,
        language
      );

      // Some services don't properly handle whitespace, but do handle
      // br tags. Also, Slate converts \n in child nodes to br, so
      // this is more consistent with non-highlighted blocks.
      const hasTrailingNewline = highlightedText.endsWith('\n');
      highlightedText = highlightedText.replace(/\n/g, '<br/>');
      if (hasTrailingNewline) {
        // Add trailing break to be consistent with how Slate handles
        // newlines at the end of a block.
        highlightedText += '<br/>';
      }

      return (
        <pre style={style}>
          <code
            data-language={language}
            dangerouslySetInnerHTML={{ __html: highlightedText }}
          />
        </pre>
      );
    } else {
      return (
        <pre style={style}>
          <code>
            {children}
            {isLineBreak(node) && <br />}
          </code>
        </pre>
      );
    }
  }

  static deserialize({
    el,
    markAttributes,
    next,
    context,
  }: DeserializeArguments) {
    if (!isHTMLElementNode(el) || el.tagName.toLowerCase() !== 'pre') return;

    const hasCode =
      el.children.length === 1 &&
      el.children[0].tagName.toLowerCase() === 'code';

    if (!hasCode && el.getAttribute('ccp_infra_version')) {
      // This pre comes from Excel Web, and we just want to skip it
      // to get the table underneath.
      return;
    }

    const data = {} as any;

    const alignment = AlignmentUtils.alignmentDataFromHtmlElement(el);
    if (alignment) {
      data.alignment = alignment;
    }

    // Plain PRE block.
    if (!hasCode) {
      breaksToNewlines(el);

      context.preDepth ||= 0;
      context.preDepth++;
      const children = next(el.childNodes, markAttributes);
      context.preDepth--;

      return {
        type: 'code',
        children,
        ...data,
      };
    }

    const codeNode: HTMLElement = el.children[0] as HTMLElement;
    let language = codeNode.dataset.language;

    if (!language) {
      for (const className of codeNode.classList) {
        if (className.startsWith('language-')) {
          language = className.slice('language-'.length);
        }
      }
    }

    if (!language) {
      for (const className of codeNode.classList) {
        if (className.startsWith('lang-')) {
          language = className.slice('lang-'.length);
        }
      }
    }

    if (language) data.code_language = language;

    breaksToNewlines(codeNode);

    context.preDepth ||= 0;
    context.preDepth++;
    const children = next(codeNode.childNodes, markAttributes);
    context.preDepth--;

    return {
      type: 'code',
      children,
      ...data,
    };
  }
}

export default SerializerCodeBlock;
